import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import type { TypographyProps } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';
import { useGetEmailOptOutsQuery } from '@/state/queries/email-preferences/use-get-email-opt-outs';
import { useRemoveEmailOptOutMutation } from '@/state/mutations/email-preferences/use-remove-email-opt-out';
import ConfirmationModal from '@/components/common/confirmation-modal';

type BlockedOrganizationsTabProps = {
  tabTitle?: TypographyProps;
};

const BlockedOrganizationsTab: React.FC<BlockedOrganizationsTabProps> = ({ tabTitle }) => {
  const t = useTranslations();
  const notifications = useNotifications();
  const { data: optOuts, isLoading, error } = useGetEmailOptOutsQuery();
  const { mutateAsync: removeOptOut, isPending } = useRemoveEmailOptOutMutation();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedOptOut, setSelectedOptOut] = useState<{ id: string; orgName: string; email: string } | null>(null);

  const handleUnblock = async () => {
    if (!selectedOptOut) return;
    try {
      await removeOptOut(selectedOptOut.id);
      notifications.notifySuccess(
        t('emailPreferences.unblockSuccess', {
          organization: selectedOptOut.orgName,
          email: selectedOptOut.email,
        }),
      );
    } catch {
      notifications.notifyError(t('emailPreferences.unblockError'));
    } finally {
      setConfirmModalOpen(false);
      setSelectedOptOut(null);
    }
  };

  const onConfirmUnblock = () => {
    void handleUnblock();
  };

  return (
    <Box sx={{ p: 2, pt: 0 }}>
      <Typography {...tabTitle}>{t('emailPreferences.title')}</Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('emailPreferences.description')}
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('emailPreferences.loadError')}
        </Alert>
      )}

      {!isLoading && !error && (!optOuts || optOuts.length === 0) && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {t('emailPreferences.noBlocked')}
        </Typography>
      )}

      {optOuts && optOuts.length > 0 && (
        <List sx={{ py: 0 }}>
          {optOuts.map((optOut) => (
            <ListItem key={optOut.id} sx={{ py: 0.5 }}>
              <ListItemText
                primary={optOut.organization?.name ?? t('emailPreferences.unknownOrganization')}
                primaryTypographyProps={{ variant: 'body2' }}
                secondary={
                  <Typography variant="caption" color="text.secondary" component="span">
                    {optOut.email} · {t('emailPreferences.blockedSince')}{' '}
                    {new Date(optOut.createdAt).toLocaleDateString()}
                  </Typography>
                }
                secondaryTypographyProps={{ component: 'div' }}
              />
              <Tooltip title={t('emailPreferences.unblock')}>
                <IconButton
                  edge="end"
                  onClick={() => {
                    setSelectedOptOut({
                      id: optOut.id,
                      orgName: optOut.organization?.name ?? t('emailPreferences.unknownOrganization'),
                      email: optOut.email,
                    });
                    setConfirmModalOpen(true);
                  }}
                  disabled={isPending}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      )}

      <ConfirmationModal
        open={confirmModalOpen}
        onCancel={() => {
          setConfirmModalOpen(false);
          setSelectedOptOut(null);
        }}
        onConfirm={onConfirmUnblock}
        title={t('emailPreferences.unblockModal.title')}
        text={t('emailPreferences.unblockModal.text', {
          organization: selectedOptOut?.orgName ?? '',
          email: selectedOptOut?.email ?? '',
        })}
      />
    </Box>
  );
};

export default BlockedOrganizationsTab;
